import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { NOMS_LANGUES, t, UI_LANGUAGES, type UiLanguage } from '../i18n';
import { relanceNecessaire } from '../i18n/sens';
import { useLanguageStore } from '../store/languageStore';
import { confirmer } from '../utils/confirmation';
import { colors, radius, spacing, typography } from '../theme/tokens';

/**
 * Sélecteur de la langue de l'INTERFACE. Vit dans les Réglages : c'est un choix qu'on fait une
 * fois, pas une action.
 *
 * À ne pas confondre avec OutputLanguagePicker, qui choisit la langue d'un DOCUMENT produit. Les
 * deux axes sont indépendants : on peut naviguer en anglais et générer un CV en français. C'est ce
 * que dit le libellé d'aide, et c'est pour ça qu'il est là.
 *
 * Changer la langue re-monte l'arbre de navigation (clé dans App) : aucun fragment d'écran ne peut
 * rester dans l'ancienne langue. Passer à l'arabe, ou le quitter, relance l'application (sens
 * d'écriture, `i18n/sens.ts`).
 *
 * Chaque langue est écrite dans sa propre langue : on retrouve la sienne même sans lire l'interface.
 */
/**
 * Passer à l'arabe, ou le quitter, redémarre l'application : on prévient d'abord, dans la langue
 * affichée. Les autres changements restent immédiats.
 */
export function choisirLangue(option: UiLanguage, actuelle: UiLanguage, change: (l: UiLanguage) => Promise<void>): void {
  if (option === actuelle) return;
  if (!relanceNecessaire(option)) {
    void change(option);
    return;
  }
  confirmer(t('settings.language.relanceTitre'), t('settings.language.relanceTexte'), [
    { text: t('common.cancel'), style: 'cancel' },
    { text: t('settings.language.relanceOk'), onPress: () => void change(option) },
  ]);
}

export function UiLanguagePicker() {
  const { language, change } = useLanguageStore();

  return (
    <View style={styles.container} testID="settings-language">
      <Text style={styles.label}>{t('settings.language.label')}</Text>
      <View style={styles.chips}>
        {UI_LANGUAGES.map((option: UiLanguage) => {
          const active = option === language;
          return (
            <Pressable
              key={option}
              testID={`settings-language-${option}`}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => choisirLangue(option, language, change)}
              style={[styles.chip, active && styles.chipActive]}
            >
              <Text style={[styles.chipText, active && styles.chipTextActive]}>
                {NOMS_LANGUES[option]}
              </Text>
            </Pressable>
          );
        })}
      </View>
      <Text style={styles.hint}>{t('settings.language.hint')}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm, paddingHorizontal: spacing.lg, paddingVertical: spacing.md },
  label: { fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight, color: colors.night },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  chipActive: { backgroundColor: colors.electric, borderColor: colors.electric },
  chipText: { fontSize: typography.small.size, color: colors.grey700 },
  chipTextActive: { color: colors.white, fontWeight: '700' },
  hint: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
});
